// Copyright (c) Bentley Systems
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getErrorMessageFromResponse,
  getJsonFromParameters,
  getParametersFromUrlString,
  getQueryParam,
  getUrlBasedOnEnv,
  setErrorState,
} from "../Helpers/helper-functions";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import {
  Alert,
  Button,
  FileEmptyCard,
  FileUpload,
  FileUploadCard,
  ProgressRadial,
  Text,
  useToaster,
} from "@itwin/itwinui-react";
import { SvgProgressBackward, SvgUpload } from "@itwin/itwinui-icons-react";
import { selectSideMenuWidthWithMargin } from "../app/header-slice";
import LabeledInputComponent from "../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../Helpers/enumerators";
import { ArchiveAddVm } from "./Models/archive-add-vm";

const ArchiveAdd = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const { id, params } = useParams();
  const accessToken = useAppSelector(selectAccessToken);
  const [files, setFiles] = useState<Array<File>>([]);
  const [name, setName] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [vm, setvm] = useState<ArchiveAddVm>();
  const urlParameters = useMemo(
    () => getParametersFromUrlString(params),
    [params],
  );
  const iTwinQueryParam = getQueryParam("iTwinId");

  const toaster = useToaster();

  const displayWarningToast = (
    infoMsg: string,
    persisting: boolean = false,
    duration: number = 7000,
  ) => {
    if (infoMsg) {
      toaster.warning(infoMsg, {
        duration,
        hasCloseButton: true,
        type: persisting ? "persisting" : "temporary",
      });
    }
  };

  const uploadAbortControllerRef = useRef<AbortController | null>(null);

  const handleUpload = async () => {
    setLoading(true);
    if (files.length === 1) {
      const formData = new FormData();
      const fileToUpload = files[0];

      const allowedExtensions =
        vm?.isMappingUpload === true
          ? ["xlsx"]
          : (
              window.env?.VITE_FILES_ALLOWEDTYPES ??
              import.meta.env.VITE_FILES_ALLOWEDTYPES
            ).split(",");
      const maxFileSizeMb = parseInt(
        window.env?.VITE_FILES_MAXSIZE ??
          import.meta.env.VITE_FILES_MAXSIZE,
        10,
      );
      const maxFileSize = maxFileSizeMb * 1024 * 1024;

      const fileSize = fileToUpload.size;
      const fileExtension =
        fileToUpload.name.split(".").pop()?.toLowerCase() || "";

      if (fileSize > maxFileSize) {
        displayWarningToast(
          `File size exceeds the maximum limit of ${maxFileSizeMb}MB.`,
        );
      } else if (!allowedExtensions.includes(fileExtension)) {
        displayWarningToast(
          `Invalid file type. Please upload a file with the following extensions: ${ 
            allowedExtensions.join(", ")}`,
        );
      } else {
        formData.append("FileDetails", fileToUpload);
        if (name !== undefined) {
          formData.append("Name", name);
        }
        if (description !== undefined) {
          formData.append("Description", description);
        }
        if (urlParameters.length > 0) {
          const paramsJson = getJsonFromParameters(urlParameters);
          formData.append("ParametersJson", paramsJson);
        }
        const runId = crypto.randomUUID();

        // Create AbortController for this upload
        uploadAbortControllerRef.current = new AbortController();

        try {
          const url = getUrlBasedOnEnv(
            `/api/Data/SaveArchiveDocument/${encodeURIComponent(id ?? "")}`,
          );

          const result = await fetch(url, {
            method: "POST",
            headers: {
              Authorization: accessToken,
              RunId: runId,
              ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
            },
            body: formData,
            signal: uploadAbortControllerRef.current.signal,
          });

          if (result.ok) {
            let navigateStr = `/archive/${  id  }/${  params}`;
            navigateStr = navigateStr + window.location.search;
            void navigate(navigateStr);
          } else if (result.status === 400) {
            // Validation failures (e.g. bad Excel headers) are shown inline rather than sending the user to the error page.
            const message = await getErrorMessageFromResponse(result);
            displayWarningToast(message);
          } else {
            const errorRunId = result.headers.get("X-Run-Id") ?? runId;
            setErrorState(result.status, dispatch, errorRunId);
          }
        } catch (error: any) {
          if (error?.name !== "AbortError") {
            setErrorState(0, dispatch, runId);
          }
        }
      }
    } else if (files.length > 1) {
      displayWarningToast("Only one file can be uploaded at a time.");
    } else {
      displayWarningToast("Please select a file to upload.");
    }
    setLoading(false);
  };

  useMemo(() => {
    if (
      name !== undefined &&
      name !== "" &&
      name !== null &&
      files.length > 0
    ) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [name, files]);

  useEffect(() => {
    setLoading(true);
    const abortController = new AbortController();
    const jsonBody = "{}";

    const urlStructure = getUrlBasedOnEnv(
      `/api/Structure/GetNodeConfiguration/${encodeURIComponent(id ?? "")}`,
    );
    const confRunId = crypto.randomUUID();
    fetch(urlStructure, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: confRunId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      body: jsonBody,
      signal: abortController.signal,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, response.headers.get("X-Run-Id") ?? confRunId);
          return;
        }
        return response.json();
      })
      .then(function (data) {
        if (!data) {
          return;
        }
        setLoading(false);
        const viewModel = new ArchiveAddVm(data);
        setvm(viewModel);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, confRunId, true);
        }
      })
      .finally(() => setLoading(false));

    // Cleanup function
    return () => {
      abortController.abort();
      // Also abort any ongoing upload when component unmounts
      if (uploadAbortControllerRef.current) {
        uploadAbortControllerRef.current.abort();
      }
    };
  }, [accessToken, dispatch, id]);

  const emptyCard = (
    <FileEmptyCard>
      <FileEmptyCard.Icon>
        <SvgUpload />
      </FileEmptyCard.Icon>
      <FileEmptyCard.Text>
        <FileUploadCard.InputLabel>Upload file</FileUploadCard.InputLabel>
        <div>Drag your file here to upload</div>
      </FileEmptyCard.Text>
    </FileEmptyCard>
  );

  return (
    <div style={{ height: "100%" }}>
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div>
            <div className="becs-infopanelbody-footer-line">
              <div className="becs-all-margin">
                <div className="rowWrap">
                  <Button styleType="borderless" onClick={() => { void navigate(-1); }}>
                    <SvgProgressBackward />
                  </Button>
                  <Text variant="title">Add new document</Text>
                </div>
                {vm?.alertText && (
                  <div className="becs-tdouble-margin">
                    <Alert>{vm.alertText}</Alert>
                  </div>
                )}
              </div>
            </div>
            <div
              className="becs-infopanelbody-footer-line"
              style={{
                display: "flex",
                justifyContent: "center",
                flex: 1,
                width: `calc(100vw - ${sideMenuWidth}px)`,
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <div
                className="becs-all-margin"
                style={{
                  width: `calc(100vw - ${sideMenuWidth}px)`,
                }}
              >
                <div className="becs-t-margin">
                  <LabeledInputComponent
                    required={true}
                    validationType={EfFieldValidationTypeEnum.Default}
                    displayStyle="default"
                    label="Name"
                    onChange={(currentValue) => {
                      setName(currentValue.target.value);
                    }}
                    onBlur={(currentValue) => {
                      setName(currentValue.target.value);
                    }}
                    placeholder="Enter text here..."
                    key="LiName"
                  />
                </div>
                <div className="becs-t-margin">
                  <LabeledInputComponent
                    displayStyle="default"
                    validationType={EfFieldValidationTypeEnum.Default}
                    label="Description"
                    onChange={(currentValue) => {
                      setDescription(currentValue.target.value);
                    }}
                    onBlur={(currentValue) => {
                      setDescription(currentValue.target.value);
                    }}
                    placeholder="Enter text here..."
                    key="LiDesc"
                  />
                </div>
                <div className="becs-t-margin">
                  <div key="cic">
                    <div className="becs-input-grid">
                      <div className="becs-input-label required">File</div>
                    </div>
                    <FileUpload
                      onFileDropped={(droppedFile) => {
                        setFiles(Array.from(droppedFile));
                      }}
                    >
                      <FileUploadCard
                        files={files}
                        onFilesChange={(changedFiles) => {
                          setFiles(changedFiles);
                        }}
                        emptyCard={emptyCard}
                      />
                    </FileUpload>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                className="becs-all-margin"
                style={{ display: "inline-block" }}
              >
                <div className="rowWrap">
                  <Button
                    styleType="cta"
                    onClick={handleUpload}
                    disabled={!isFormValid}
                  >
                    Save
                  </Button>
                  {/* <Button styleType="default">Cancel</Button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchiveAdd;
